import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/core";
import { graphql, Link } from "gatsby";
import Img, { FluidObject } from "gatsby-image";
import { LearningCenterQuery } from "../../../graphql-types";

import Layout from "../../components/layout";
import SEO from "../../components/seo";
import { ReactComponent as ArrowRightIco } from "../../images/icons/arrow-right.svg";
import { BREAKPOINTS, COLORS } from "../../ui/variables";
import Posts from "../../components/learning-center/Posts";
import Heading1 from "../../ui/Heading1";
import Heading2 from "../../ui/Heading2";
import ParagraphLarge from "../../ui/ParagraphLarge";
import Categories from "../../components/learning-center/Categories";
import CategoryNavigation from "../../components/learning-center/CategoryNavigation";

const Container = styled.div`
  max-width: 1242px;
  padding: 0 40px;
  margin: auto;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 0 20px;
  }
`;

export const Category = styled.span`
  text-transform: uppercase;
  color: ${COLORS.DARK_GRAY};
  font-size: 14px;
`;

const WelcomeHeader = styled.header`
  position: relative;
  margin-bottom: 120px;

  &::before {
    content: " ";
    top: 0;
    left: 0;
    width: 100%;
    display: block;
    position: absolute;
    background: ${COLORS.ORANGE};
    top: 0;
    left: 0;
    height: 60%;
    z-index: -1;

    @media ${BREAKPOINTS.MOBILE} {
      height: 50%;
    }
  }
`;

const WelcomeContent = styled.div`
  max-width: 1242px;
  padding: 80px 0;
  margin: auto;
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  line-height: 26px;

  @media screen and (max-width: 1242px) {
    padding: 80px 40px;
  }

  @media ${BREAKPOINTS.TABLET} {
    padding: 80px 30px;
  }

  @media ${BREAKPOINTS.MOBILE} {
    padding: 60px 20px;
  }
`;

const Feature = styled.div`
  max-width: 918px;
  width: 100%;
  margin: auto;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 697px;
  }

  @media ${BREAKPOINTS.MOBILE} {
    padding: 0 20px;
    max-width: 611px;
  }
`;

const FeatureContent = styled.div`
  max-width: 600px;
  width: 100%;
  text-align: center;
  margin: auto;
  margin-top: 60px;

  @media ${BREAKPOINTS.MOBILE} {
    margin-top: 30px;
  }
`;

const Navigation = styled.div`
  max-width: 1242px;
  margin: auto;
`;

export const pageQuery = graphql`
  query LearningCenter {
    allContentfulLearningCenterArticle(
      sort: { fields: createdAt, order: DESC }
    ) {
      nodes {
        categories {
          name
          urlSlug
        }
        title
        urlSlug
        featureImage {
          title
          fluid(maxWidth: 920) {
            ...GatsbyContentfulFluid_withWebp
          }
        }
        featureText {
          featureText
        }
      }
    }
    contentfulLearningCenterConfig {
      displayCategories {
        urlSlug
        name
      }
    }
    headerImage: file(relativePath: { eq: "graphics/high-five.png" }) {
      childImageSharp {
        fluid(maxWidth: 270) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

export interface LearningCenterPost {
  title: string;
  category: {
    name: string;
  };
  featureImage: {
    fluid: FluidObject,
    title: string,
  }
  featureText: string;
  path: string;
}

export interface LearningCenterCategory {
  name: string;
  path: string;
  posts: {
    title: string;
    path: string;
    featureText: string;
    featureImage: {
      fluid: FluidObject,
      title: string,
    }
    category: {
      name: string;
      urlSlug: string;
    };
  }[];
}

const LearningCenterPage = ({ data }: { data: LearningCenterQuery }) => {
  const [feature, setFeature] = useState<LearningCenterPost>(null);
  const [posts, setPosts] = useState<LearningCenterPost[]>(null);
  const [categories, setCategories] = useState<LearningCenterCategory[]>(null);

  const {
    allContentfulLearningCenterArticle: rawArticles,
    contentfulLearningCenterConfig: config,
  } = data;

  const rawCategories = config.displayCategories;

  useEffect(() => {
    const formattedArticles: LearningCenterCategory["posts"] = rawArticles.nodes.map(
      article => ({
        title: article.title,
        featureText: article.featureText.featureText,
        path: `/learning-center/${article.categories[0].urlSlug}/${article.urlSlug}`,
        featureImage: {
          fluid: article.featureImage.fluid,
          title: article.featureImage.title,
        },
        category: {
          name: article.categories[0].name,
          urlSlug: article.categories[0].urlSlug,
        },
      })
    );

    const categories = rawCategories.map(category => {
      const articles = formattedArticles.filter(
        article => article.category.urlSlug === category.urlSlug
      );

      let twoPosts: LearningCenterCategory["posts"] = [];
      if (articles && articles.length > 0) {
        twoPosts = articles.slice(0, 2);
      }

      return {
        name: category.name,
        path: `/learning-center/${category.urlSlug}`,
        posts: twoPosts,
      };
    });

    setFeature(formattedArticles[0]);
    setPosts(formattedArticles.slice(1, 10));
    setCategories(categories);
  }, [rawArticles, rawCategories]);

  const navigationCategories = config.displayCategories.map(category => ({
    name: category.name,
    urlSlug: category.urlSlug,
  }));

  if (!feature) {
    return null;
  }

  return (
    <Layout>
      <SEO
        title="Learning Center"
        description="The Small Door Learning Center is a place to gain knowledge on the latest information in veterinary medicine from trusted sources."
      />

      <Navigation>
        <CategoryNavigation
          activeSlug="All"
          categories={navigationCategories}
        />
      </Navigation>
      <WelcomeHeader>
        <WelcomeContent>
          <div
            css={css`
              max-width: 600px;
            `}
          >
            <Heading1>Learning Center</Heading1>
            Welcome to our Learning Center, your one-stop for everything you
            need to know about caring for your pet. Check out our articles on
            pet health, wellness, behavior and more, written by our expert
            veterinarians.
          </div>
          <div
            css={css`
              @media screen and (max-width: 600px) {
                display: none;
              }
            `}
          >
            <Img
              css={css`
                width: 270px;
              `}
              fluid={data.headerImage.childImageSharp.fluid}
            />
          </div>
        </WelcomeContent>

        <Feature>
          <Img
            css={css`
              width: 100%;
            `}
            fluid={feature.featureImage.fluid}
            alt={feature.featureImage.title}
          />
          <FeatureContent>
            <div
              css={css`
                text-align: center;
                text-transform: uppercase;
                color: ${COLORS.DARK_GRAY};
                margin-bottom: 16px;
              `}
            >
              {feature.category.name}
            </div>
            <Heading2>{feature.title}</Heading2>
            <ParagraphLarge>{feature.featureText}</ParagraphLarge>
            <Link
              css={css`
                display: block;
                font-size: 18px;
                font-weight: 500;
              `}
              to={feature.path}
            >
              Read more{" "}
              <ArrowRightIco
                width="14"
                height="11"
                css={css`
                  fill: ${COLORS.ORANGE};
                `}
              />
            </Link>
          </FeatureContent>
        </Feature>
      </WelcomeHeader>

      <Container>
        <Posts posts={posts} />
      </Container>

      <Categories categories={categories} />
    </Layout>
  );
};

export default LearningCenterPage;
